/**
 * Types du domaine équipe et pointage.
 */
import type { Role } from "../../context.js";
import { round2 } from "../../money.js";

/**
 * Nature d'une heure pointée.
 *
 * Le registre légal veut toutes les heures, y compris celles qui ne sont pas
 * travaillées : un décompte qui ferait disparaître les absences ne prouverait
 * rien. `weather` est l'intempérie, qui en bâtiment n'a rien de marginal.
 */
export type TimeEntryKind =
  | "work"
  | "travel"
  | "weather"
  | "paid_leave"
  | "unpaid_leave"
  | "sick"
  | "training";

/**
 * Ce qui compte comme temps de travail au sens du décompte.
 *
 * La formation en est : c'est du temps de travail effectif. Les intempéries
 * n'en sont pas — le salarié est indemnisé sans travailler.
 */
const WORKED: ReadonlySet<TimeEntryKind> = new Set<TimeEntryKind>([
  "work",
  "travel",
  "training",
]);

/**
 * Ce qui est imputé au coût d'un chantier.
 *
 * Plus étroit que le temps de travail, et volontairement : seul `work` est
 * une heure passée sur l'ouvrage. Cette liste double celle de
 * `recalc_project_costs` en SQL, ce qui est assumé — la base doit pouvoir
 * calculer sans le TypeScript — mais les deux doivent bouger ensemble.
 */
const BILLABLE: ReadonlySet<TimeEntryKind> = new Set<TimeEntryKind>(["work"]);

export function isWorkedTime(kind: TimeEntryKind): boolean {
  return WORKED.has(kind);
}

export function isProjectCost(kind: TimeEntryKind): boolean {
  return BILLABLE.has(kind);
}

export interface Employee {
  id: string;
  firstName: string;
  lastName: string;
  jobTitle: string | null;
  phone: string | null;
  /** Durée hebdomadaire de référence. Situe le total, ne calcule pas la paie. */
  weeklyHours: number;
  hiredOn: string | null;
  isActive: boolean;
  /** Compte applicatif, quand il y en a un. La plupart des ouvriers n'en ont pas. */
  userId: string | null;
  /**
   * Coût horaire chargé. `null` **ne veut pas dire zéro** : soit il n'est pas
   * renseigné, soit l'appelant n'a pas le droit de le lire — la policy
   * `patron_only` réserve la table au patron.
   */
  hourlyCost: number | null;
}

/**
 * Une invitation : le code par lequel un salarié obtient un compte.
 *
 * Le code lui-même n'y figure pas, et ne peut pas y figurer — la base n'en
 * garde que le condensat. Il n'existe qu'une fois, dans la réponse à
 * l'émission. Ce type décrit donc ce qu'on peut encore savoir d'une
 * invitation *après* l'avoir remise : à qui, pour quel rôle, jusqu'à quand,
 * et si elle a servi.
 */
export interface Invitation {
  id: string;
  employeeId: string | null;
  role: Role;
  expiresAt: string;
  claimedAt: string | null;
  revokedAt: string | null;
  createdAt: string;
}

/** `live` : le code court encore et n'a pas servi. */
export type InvitationStatus = "live" | "claimed" | "revoked" | "expired";

export function invitationStatus(
  invitation: Pick<Invitation, "claimedAt" | "revokedAt" | "expiresAt">,
  now: Date = new Date(),
): InvitationStatus {
  if (invitation.claimedAt) return "claimed";
  if (invitation.revokedAt) return "revoked";
  // L'expiration se juge sur l'horloge, pas sur une colonne d'état : rien ne
  // repasse en base pour marquer un code périmé, et un balayage nocturne
  // serait un mécanisme de plus pour une information déjà portée par la date.
  if (Date.parse(invitation.expiresAt) <= now.getTime()) return "expired";
  return "live";
}

/** Où en est un salarié vis-à-vis de l'application. */
export type EmployeeAccess = "active" | "invited" | "none";

/**
 * L'état d'accès d'un salarié, tel que l'écran Équipe l'annonce.
 *
 * `active` d'abord, et l'ordre compte : un salarié relié à un compte l'est,
 * même si une vieille invitation traîne derrière lui.
 */
export function employeeAccess(
  employee: Pick<Employee, "id" | "userId">,
  invitations: readonly Invitation[],
  now: Date = new Date(),
): EmployeeAccess {
  if (employee.userId) return "active";
  const live = invitations.some(
    (i) => i.employeeId === employee.id && invitationStatus(i, now) === "live",
  );
  return live ? "invited" : "none";
}

export interface TimeEntry {
  id: string;
  employeeId: string;
  projectId: string | null;
  projectName: string | null;
  workDate: string;
  hours: number;
  kind: TimeEntryKind;
  /** Figé à la saisie. Voir `freeze_hourly_cost` côté base. */
  hourlyCost: number | null;
  /**
   * Surcoût de majoration imputé à ce pointage, en euros.
   *
   * Calculé **en base** (`recalc_overtime_week`) et seulement lu ici : la
   * règle — tranches légales, taux réglables, heures empilées dans l'ordre de
   * la semaine — n'a qu'un exemplaire, celui qui fait aussi le coût des
   * chantiers. Zéro pour tout ce qui n'est pas du travail sur un ouvrage.
   */
  overtimePremium: number;
  notes: string | null;
}

export interface DayTally {
  /** AAAA-MM-JJ. */
  date: string;
  workedHours: number;
  absenceHours: number;
}

/**
 * Le décompte d'un salarié sur une semaine.
 *
 * C'est la forme qu'exige L.3171-2 : le détail jour par jour, et le
 * récapitulatif hebdomadaire.
 */
export interface WeeklyTally {
  employeeId: string;
  /** Lundi de la semaine, AAAA-MM-JJ. */
  weekStart: string;
  /** Sept entrées, du lundi au dimanche, y compris les jours à zéro. */
  days: DayTally[];
  workedHours: number;
  absenceHours: number;
  contractualHours: number;
  /** Heures au-delà de la durée contractuelle. */
  overtimeHours: number;
  /**
   * Coût des heures imputables dont le taux est connu, **majoration comprise**.
   *
   * Le patron : « certes la majoration relève de la paye, mais nous en avons
   * besoin ici pour la rentabilité d'un chantier ». Ne pas la compter faisait
   * paraître plus rentable le chantier tenu en poussant l'équipe à 45 heures —
   * exactement celui dont il faut connaître le vrai coût. Ce n'est pas une
   * paie : ni bulletin, ni repos compensateur ; un coût qui ne ment plus.
   */
  labourCost: number;
  /** La part de `labourCost` due à la majoration. */
  overtimePremium: number;
  /**
   * Heures imputables sans taux horaire.
   *
   * Exposées séparément plutôt que comptées à zéro : un chantier affichant
   * « 0 € de main-d'œuvre » sur des heures réelles tromperait davantage qu'un
   * total annoncé comme incomplet.
   */
  unpricedHours: number;
}

const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * Le lundi de la semaine d'une date.
 *
 * Tout se fait en UTC sur une date nue : construire un `Date` local à partir
 * d'un `AAAA-MM-JJ` puis le reformater décale d'un jour selon le fuseau, et
 * une heure qui change de jour change de semaine — donc de total
 * hebdomadaire, donc de décompte légal. C'est la même faute que celle déjà
 * corrigée sur les bornes de mois et sur l'échéance des factures.
 */
export function startOfWeek(isoDate: string): string {
  const t = Date.parse(`${isoDate.slice(0, 10)}T00:00:00Z`);
  const d = new Date(t);
  // getUTCDay : 0 = dimanche. En France la semaine commence le lundi.
  const shift = (d.getUTCDay() + 6) % 7;
  return new Date(t - shift * DAY_MS).toISOString().slice(0, 10);
}

/** Les sept dates de la semaine, du lundi au dimanche. */
export function weekDates(weekStart: string): string[] {
  const t = Date.parse(`${weekStart.slice(0, 10)}T00:00:00Z`);
  return Array.from({ length: 7 }, (_, i) =>
    new Date(t + i * DAY_MS).toISOString().slice(0, 10),
  );
}

/**
 * Le décompte hebdomadaire d'un salarié.
 *
 * Les jours sans pointage figurent avec des zéros : un registre à trous ne
 * distingue pas « n'a pas travaillé » de « on a oublié de saisir », et c'est
 * précisément la distinction qu'un contrôle cherche.
 */
export function computeWeeklyTally(
  employee: Pick<Employee, "id" | "weeklyHours">,
  weekStart: string,
  entries: readonly TimeEntry[],
): WeeklyTally {
  const dates = weekDates(weekStart);
  const mine = entries.filter(
    (e) => e.employeeId === employee.id && dates.includes(e.workDate.slice(0, 10)),
  );

  const days: DayTally[] = dates.map((date) => {
    const duJour = mine.filter((e) => e.workDate.slice(0, 10) === date);
    return {
      date,
      workedHours: round2(
        duJour.filter((e) => isWorkedTime(e.kind)).reduce((s, e) => s + e.hours, 0),
      ),
      absenceHours: round2(
        duJour.filter((e) => !isWorkedTime(e.kind)).reduce((s, e) => s + e.hours, 0),
      ),
    };
  });

  const workedHours = round2(days.reduce((s, d) => s + d.workedHours, 0));
  const absenceHours = round2(days.reduce((s, d) => s + d.absenceHours, 0));

  const imputables = mine.filter((e) => isProjectCost(e.kind));
  const overtimePremium = round2(
    imputables
      .filter((e) => e.hourlyCost != null)
      .reduce((s, e) => s + (e.overtimePremium ?? 0), 0),
  );
  const labourCost = round2(
    imputables
      .filter((e) => e.hourlyCost != null)
      .reduce((s, e) => s + e.hours * (e.hourlyCost ?? 0), 0) + overtimePremium,
  );
  const unpricedHours = round2(
    imputables.filter((e) => e.hourlyCost == null).reduce((s, e) => s + e.hours, 0),
  );

  return {
    employeeId: employee.id,
    weekStart,
    days,
    workedHours,
    absenceHours,
    contractualHours: employee.weeklyHours,
    overtimeHours: round2(Math.max(0, workedHours - employee.weeklyHours)),
    labourCost,
    overtimePremium,
    unpricedHours,
  };
}

/** Nom d'affichage d'un salarié, cohérent partout. */
export function employeeDisplayName(
  employee: Pick<Employee, "firstName" | "lastName">,
): string {
  return [employee.firstName, employee.lastName].filter(Boolean).join(" ").trim() || "Sans nom";
}
